import { useState } from 'react';
import {
  CasRenderBoolTrue,
  CasRenderBoolTrueFalse,
  CasRenderDate,
  CasTable,
} from 'src/compass/shared/components';
import prependAllOption from 'src/compass/shared/util/prependAllOption';
import { StringsToOptions } from 'src/compass/shared/util/StringsToOptions';
import { ICasTable } from 'src/compass/shared/components/CasTable/CasTable';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import { CasYesNoSelect } from 'src/compass/application';

import RenderInsuredName from './RenderInsuredName';
import { RenderLastActivity } from './RenderLastActivity';
import RenderDeadReports from './RenderDeadReports';

import ClaimDetailsDialog from '../ClaimDetails/ClaimDetailsDialog';
import DeadReportsDialog from '../ClaimDetails/DeadReports/DeadReportsDialog';

type ClaimListingProps = ICasTable & {
  autoCheckout?: boolean;
  openDeadReports?: boolean;
}

export const CLAIM_STATUS_DONE_BG_COLOR = '#BFBFBF';

export enum ClaimStatus {
  Send = 'Send',
  DocusignInProcess = 'Docusign In Process',
  DocusignReview = 'Docusign Review',
  Approved = 'Approved',
  Attached = 'Attached',
  Callback = 'Callback',
  Done = 'Done',
}

export const ClaimStatusFilter_Empty = '(Empty)';
export const ClaimStatusFilter_ApprovedOrAttached = '(Approved or Attached)';
export const ClaimStatusFilter_NotDoneOrAttached = '(Not Done or Attached)';

const DAYS_LEFT_OPTIONS = prependAllOption(StringsToOptions(['1','2', '3','7', '10', '20']));
const HAS_CLAIM_OPTIONS = prependAllOption([{id: false, name: 'No Claim'}, {id: true, name: 'Claim'}]);

export const CLAIM_STATUS_OPTIONS = prependAllOption(StringsToOptions([
  ClaimStatusFilter_ApprovedOrAttached,
  ClaimStatusFilter_Empty,
  '(Not Done)',
  ClaimStatusFilter_NotDoneOrAttached,
  ClaimStatus.Approved,
  ClaimStatus.Attached,
  ClaimStatus.Callback,
  ClaimStatus.Done,
  ClaimStatus.DocusignInProcess,
  ClaimStatus.DocusignReview,
  ClaimStatus.Send
]));

export const getColumnColorByReinsuranceYear = (row: Record<string, any>) => ({
  backgroundColor: row?.reinsuranceYearColor,
});

export const getColumnColorByDaysLeft = (row: Record<string, any>) => {
  const { daysLeft } = row;
  if (daysLeft === null) return {};
  if (daysLeft <= 10) return { backgroundColor: 'orange' };
  if (daysLeft <= 20) return { backgroundColor: 'yellow' };
  return {};
};

export const getColumnColorByStatus = (row: Record<string, any>) => {
  const { status } = row;
  if (status === 'Done') return { backgroundColor: CLAIM_STATUS_DONE_BG_COLOR };
  if (status === 'Send') return { backgroundColor: '#FFE699' };
  if (status === 'Attached') return { backgroundColor: '#FF0066' };
  return {};
};

// NOTE: Property order will be used by SearchTab
export const ClaimColumns = {
  lastActivityDate: {
    field: 'lastActivityDate',
    label: 'Last Activity',
    render: RenderLastActivity,
  },
  insuredName: { field: 'insuredName' },
  reinsuranceYear: { field: 'reinsuranceYear', sx: getColumnColorByReinsuranceYear },
  policyNumber: { field: 'policyNumber', label: 'Policy#' },
  sce: { field: 'sce' },
  claimNumber: { field: 'claimNumber', label: 'Claim#' },
  status: { field: 'status', sx: getColumnColorByStatus },
  checkedOut: { field: 'checkedOut', sortable: false },
  effectiveDate: { field: 'effectiveDate', render: CasRenderDate },
  endDate: { field: 'endDate', render: CasRenderDate },
  deadsReported: { field: 'deadsReported', render: RenderDeadReports },
  claim: { field: 'claim', label: 'Claim?', render: CasRenderBoolTrueFalse },
  daysLeft: { field: 'daysLeft', sx: getColumnColorByDaysLeft, sortable: false },
  isFwdContract: { field: 'isFwdContract', label: 'Is Fwd Contract?', render: CasRenderBoolTrue },
  noticeSentDate: { field: 'noticeSentDate', label: 'Notice Sent', render: CasRenderDate },
  claimReceivedDate: { field: 'claimReceivedDate', label: 'Received', render: CasRenderDate },
  claimAttachedDate: { field: 'claimAttachedDate', label: 'Attached', render: CasRenderDate },
  claimRejectedDate: { field: 'claimRejectedDate', label: 'Rejected', render: CasRenderDate },
  claimReviewedDate: { field: 'claimReviewedDate', label: 'Reviewed', render: CasRenderDate },
  claimNotes: { field: 'claimNotes' },
  whatSent: { field: 'whatSent', label: 'What' },
  assigned: { field: 'assigned', render: CasRenderDate },
  callbackType: { field: 'callbackType' },
  assignedTo: { field: 'assignedTo' },
  callbackNotes: { field: 'callbackNotes' },
  state: { field: 'state' },
  county: { field: 'county' },
  crop: { field: 'crop' },
  type: { field: 'type' },
  hc: { field: 'totalNumberInsured', label: 'HC' },
  targetCwt: { field: 'targetCWT', label: 'Target Cwt' },
  coveragePrice: { field: 'coveragePrice' },
};
// NOTE: Property order will be used by SearchTab
export const ClaimFilters = {
  insuredNameSearch: {
    name: 'insuredNameSearch',
    label: 'Insured search',
    autoFocus: true
  },
  policyNumber: {
    type: 'number',
    name: 'policyNumber',
    label: 'Policy#',
  },
  sce: {
    type: 'number',
    name: 'sce',
  },
  claimNumber: {
    type: 'number',
    name: 'claimNumber',
    label: 'Claim#',
  },
  daysLeftLessThan: {
    type: 'select',
    name: 'daysLeftLessThan',
    label: 'Days Left <=',
    options: DAYS_LEFT_OPTIONS,
  },
  hasClaim: {
    type: 'select',
    name: 'hasClaim',
    label: 'Has Claim?',
    options: HAS_CLAIM_OPTIONS,
  },
  status: {
    type: 'select',
    name: 'status',
    options: CLAIM_STATUS_OPTIONS,
  },
  isFwdContract: {
    type: 'component',
    name: 'isFwdContract',
    label: 'Is Fwd Contract?',
    includeAllOption: true,
    component: CasYesNoSelect,
  },
  endDate: {
    type: 'date',
    name: 'endDate',
  },
  insuredName: {
    type: 'autocomplete',
    name: 'insuredName',
    url: 'Sce/InsuredName/Select/List',
  },
};

export default function ClaimListing({
  columns: paramColumns,
  autoCheckout=true,
  openDeadReports: parmOpenDeadReports = false,
  ...rest
}: ClaimListingProps) {
  const [dialogRowId, setDialogRowId] = useState<any>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);
  const [openDeadReports, setOpenDeadReports] = useState<boolean>(parmOpenDeadReports);

  const adjustedColumns = paramColumns.map((col) =>
    col.field === 'insuredName'
      ? {
          ...col,
          render: (props: ICasRenderCellProps) => (
            <RenderInsuredName onClick={() => setDialogRowId(props.row.id)} {...props} />
          ),
        }
      : col
  );

  return (
    <>
      <CasTable entity="Sce/Claims" update={reloadTable} columns={adjustedColumns} {...rest} />
      {dialogRowId &&
        (openDeadReports ? (
          <DeadReportsDialog
            id={dialogRowId}
            onClose={() => {
              setDialogRowId(null);
              setReloadTable(new Date());
            }}
            onViewClaimDetails={() => setOpenDeadReports(false)}
          />
        ) : (
          <ClaimDetailsDialog
            autoCheckout={autoCheckout}
            id={dialogRowId}
            onClose={() => {
              setDialogRowId(null);
              setReloadTable(new Date());
              setOpenDeadReports(parmOpenDeadReports);
            }}
          />
        ))}
    </>
  );
}
